export class NumberFormat {
  public constructor(protected readonly value: string | number) {}

  public static create(value: string | number): NumberFormat {
    return new NumberFormat(value)
  }

  /**
   * Format the input number to the stats format:
   *
   * `0.12345 -> .123`
   * `1.2345 -> 1.235`
   * `-0.2345 -> -.235`
   *
   * Always adding the fixed value if it is not set.
   * Can not be used for the sorting purposes.
   *
   * @param fixed
   * @param removeLeadingZero Remove or keep the leading zero if the value start with it
   */
  public formatAfterDot(fixed: number = 3, removeLeadingZero: boolean = true): string {
    return this.formatValueAfterDot(this.value, fixed, removeLeadingZero)
  }

  /**
   * Divide the resulting number by 100 and convert it to decimal points.
   *
   * `90.2 -> .902`
   * `0.001 -> .001`
   * `-0.2345 -> -.235`
   *
   * @param fixed
   * @param removeLeadingZero
   * @param removeLeadingZero Remove or keep the leading zero if the value start with it
   */
  public percentageToDecimal(fixed: number = 3, removeLeadingZero: boolean = true): string {
    const value = Number(this.value) / 100

    return this.formatValueAfterDot(value, fixed, removeLeadingZero)
  }

  /**
   * Multiply a floating point number by 100 to convert to normal percentages.
   *
   * `.902 -> 90.2`
   * `0.001 -> 1`
   * `-.235 -> -23.5`
   *
   * @param fixed Number of decimal places
   */
  public decimalToPercentage(fixed: number = 0): string {
    const value = Number(this.value) * 100

    return value.toFixed(fixed)
  }

  /**
   * Format input value to the integer and remove any decimal points.
   */
  public integer(): string {
    return Number(this.value).toFixed(0)
  }

  private formatValueAfterDot(value: string | number, fixed: number, removeLeadingZero: boolean = true): string {
    const threeAfterDotValue = this.afterDot(value, fixed)

    if (removeLeadingZero && threeAfterDotValue[0] === '0') {
      return threeAfterDotValue.slice(1)
    }

    if (removeLeadingZero && threeAfterDotValue[0] === '-' && threeAfterDotValue[1] === '0') {
      return `-${threeAfterDotValue.slice(2)}`
    }

    return threeAfterDotValue
  }

  private afterDot(value: number | string, fixed: number): string {
    return Number(value).toFixed(fixed).toString()
  }
}
